import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Brain,
  BriefcaseBusiness,
  CheckCircle2,
  CircleX,
  ClipboardCheck,
  Clock3,
  Database,
  Download,
  FileSearch,
  FileText,
  LockKeyhole,
  MapPin,
  Mic,
  Route,
  ShieldCheck,
  UserCheck,
  Video,
} from "lucide-react";

const TALENT_POOL_CONTACT = "/talent-pool/register";

const FREE_GUIDES = [
  {
    title: "Railway Careers · CV Guide",
    blurb:
      "A concise, evidence-led guide to how train operators screen CVs for safety-critical roles. Covers structure, transferable evidence, responsibility, reliability and common weaknesses that can reduce progression.",
    pages: "2 pages",
    file: "/resources/cv-guide.pdf",
    label: "cv-guide.pdf",
  },
  {
    title: "Railway Careers · Interview Guide",
    blurb:
      "A practical overview of behavioural evidence, safety-critical decision-making, STAR responses and answers that demonstrate genuine personal responsibility.",
    pages: "2 pages",
    file: "/resources/interview-guide.pdf",
    label: "interview-guide.pdf",
  },
  {
    title: "Railway Careers · Role Aptitude Guide",
    blurb:
      "An introduction to the concentration, memory, perception, information-processing and vigilance capabilities commonly needed across operational railway roles.",
    pages: "2 pages",
    file: "/resources/psychometric-guide.pdf",
    label: "psychometric-guide.pdf",
  },
  {
    title: "Railway Careers · Online Profile Guide",
    blurb:
      "A practical guide to online applications and behavioural questionnaires. Learn how to present experience clearly, consistently and with enough evidence to support progression.",
    pages: "2 pages",
    file: "/resources/online-profile-guide.pdf",
    label: "online-profile-guide.pdf",
  },
];

const APPLICANT_PATHS = [
  {
    icon: BookOpen,
    eyebrow: "Prepare independently",
    title: "Build a stronger application",
    body:
      "Use free guides and vacancy information to improve your CV, interview evidence and understanding of railway recruitment.",
    cta: "View free guides",
    href: "#free-guides",
  },
  {
    icon: BadgeCheck,
    eyebrow: "Earn employer visibility",
    title: "Qualify for the talent pool",
    body:
      "Complete a role-specific sequence of assessment gateways. Only candidates who meet every required standard progress into the employer-accessible pool.",
    cta: "See how the pool works",
    href: "#talent-pool",
    featured: true,
  },
];

const TALENT_GATEWAYS = [
  {
    number: "01",
    icon: MapPin,
    title: "Eligibility and motivation checks",
    body:
      "Stage One confirms general eligibility to enter the Railway Careers talent pool before any role-specific assessment begins.",
  },
  {
    number: "02",
    icon: Brain,
    title: "Target role selection",
    body:
      "Applicants who pass Stage One progress to Stage Two and select the railway role or pathway they want to target.",
  },
  {
    number: "03",
    icon: ClipboardCheck,
    title: "Scenario and behaviour profile",
    body:
      "Work through realistic operational situations assessing safety judgement, risk awareness, procedural discipline, communication and accountability.",
  },
  {
    number: "04",
    icon: FileSearch,
    title: "Structured CV evidence",
    body:
      "Upload your CV so role-relevant experience can be identified, structured and checked. Your evidence also shapes later interview questions.",
  },
  {
    number: "05",
    icon: Mic,
    title: "Speech-driven interview simulator",
    body:
      "Answer adaptive role and CV-based questions aloud. The simulator tests depth, clarity and consistency rather than polished written responses.",
  },
  {
    number: "06",
    icon: Video,
    title: "Live professional validation",
    body:
      "Complete a 45–60 minute Microsoft Teams interview with an experienced recruiting manager, based on your CV, simulator answers and assessment profile.",
  },
];

const POOL_PRINCIPLES = [
  {
    icon: LockKeyhole,
    title: "Entry cannot be purchased",
    body:
      "Courses, subscriptions and coaching do not change the pass standard, assessment result or employer ranking.",
  },
  {
    icon: CircleX,
    title: "Every gateway must be passed",
    body:
      "Candidates who do not meet a compulsory standard do not advance into the employer-facing pool.",
  },
  {
    icon: UserCheck,
    title: "Final human decision",
    body:
      "An experienced recruiting manager completes the final structured review before admission.",
  },
  {
    icon: ShieldCheck,
    title: "Consistent and auditable",
    body:
      "Questions, recordings, transcripts and scoring evidence support quality assurance and accurate employer information.",
  },
];

const EMPLOYER_VISIBLE = [
  "Role and location preferences",
  "Postcode-derived travel feasibility",
  "Shift and early-start availability",
  "Role aptitude evidence",
  "Scenario and behavioural profile",
  "Structured CV evidence",
  "Interview simulator evidence",
  "Live interview outcome",
  "Assessment and profile dates",
];

const CANDIDATE_CONTROLS = [
  {
    icon: Route,
    title: "Travel matching without exposing your address",
    body:
      "Your postcode is used to calculate practical journey times. Employers can initially see the travel result rather than your full home address.",
  },
  {
    icon: BriefcaseBusiness,
    title: "Approved employer access",
    body:
      "Only approved employers using the talent pool can view admitted candidate profiles and supporting recruitment evidence.",
  },
  {
    icon: Database,
    title: "A current, credible profile",
    body:
      "Availability, CV information, travel details and role interest must be refreshed so employers are not shown outdated information.",
  },
  {
    icon: Clock3,
    title: "Clear outcomes and reassessment rules",
    body:
      "You receive a progression outcome at each gateway. Where reassessment is available, the relevant development or waiting period is explained.",
  },
];

const FAQS = [
  {
    q: "Can I pay to enter the talent pool?",
    a:
      "No. Entry is earned by passing every compulsory gateway. Paid training, subscriptions and coaching do not change the standard or your employer visibility.",
  },
  {
    q: "Does entering the pool guarantee a job?",
    a:
      "No. Employers retain responsibility for their own selection, formal assessments, medicals, checks and final hiring decision.",
  },
  {
    q: "Will I see my detailed assessment scores?",
    a:
      "You receive your progression outcome and relevant next-step information. Detailed evidence and scoring remain part of the approved employer profile and quality-assurance record.",
  },
  {
    q: "What happens if I do not pass?",
    a:
      "You do not enter the employer-accessible pool. Where appropriate, you may receive broad development information and a future reassessment date or route.",
  },
];

const STAGE_ONE_REQUIREMENTS = [
  "have the legal right to work in the UK",
  "meet the minimum working-age requirements",
  "can read, understand and communicate clearly in English",
  "have suitable basic literacy, numeracy and digital skills",
  "are prepared to undergo identity, reference and employment-history checks",
  "are willing to disclose relevant medical conditions and medication honestly",
  "understand that some railway roles involve shifts, weekends, nights and bank holidays",
  "are able to travel to work when public transport may not be available",
  "are willing to complete required training and maintain competence throughout employment",
];

function Reveal({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({
  mark,
  icon: Icon,
  title,
  accent,
  body,
}) {
  return (
    <div className="grid lg:grid-cols-12 gap-10 mb-10">
      <div className="lg:col-span-5">
        <div className="chapter-mark">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {mark}
        </div>

        <h2 className="mt-6 font-display text-ice text-3xl md:text-5xl tracking-[-0.022em] font-extrabold leading-[1.05]">
          {title} <span className="text-sky">{accent}</span>
        </h2>
      </div>

      {body && (
        <div className="lg:col-span-7 lg:pt-3">
          <p className="text-slate text-lg leading-[1.65]">
            {body}
          </p>
        </div>
      )}
    </div>
  );
}

export default function ForApplicants() {
  return (
    <div className="bg-nav min-h-screen">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-14">
        <div className="chapter-mark">For Applicants</div>

        <div className="mt-6 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-display text-ice text-4xl md:text-6xl tracking-[-0.025em] font-extrabold leading-[1.04] max-w-5xl">
              Prepare for railway careers.{" "}
              <span className="text-sky">
                Earn your place
              </span>{" "}
              in front of employers.
            </h1>

            <p className="mt-5 text-slate text-lg max-w-3xl leading-relaxed">
              Railway Careers brings vacancies, practical guidance
              and an employer-ready talent pool into one applicant
              journey. Prepare independently, or demonstrate your
              suitability through a structured sequence of
              role-specific gateways.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
              <a
                href="#talent-pool"
                className="pill pill-primary"
              >
                Explore the talent pool
                <ArrowRight className="w-4 h-4" />
              </a>

              <a
                href="#free-guides"
                className="pill pill-ghost"
              >
                Download free guides
                <Download className="w-4 h-4" />
              </a>

              <Link
                to="/vacancies"
                className="pill pill-ghost"
              >
                Search live vacancies
                <BriefcaseBusiness className="w-4 h-4" />
              </Link>

              <Link
                to="/talent-pool/login"
                className="pill pill-ghost"
                data-testid="talent-pool-signin-hero"
              >
                Talent pool sign in
                <UserCheck className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 rounded-md border border-sky/30 bg-sky/10 p-6">
            <div className="flex items-center gap-2 text-sky text-[11px] uppercase tracking-[0.18em] font-semibold">
              <ShieldCheck className="w-4 h-4" />
              Merit-based entry
            </div>

            <h2 className="mt-3 font-display text-ice text-2xl font-bold tracking-[-0.015em]">
              The talent pool is not pay to win.
            </h2>

            <p className="mt-3 text-slate text-sm leading-relaxed">
              Candidates cannot buy employer access. You must pass
              every mandatory gateway, including a final live
              interview with an experienced recruiting manager.
            </p>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {APPLICANT_PATHS.map((path, index) => {
            const Icon = path.icon;

            return (
              <Reveal
                key={path.title}
                delay={index * 0.08}
              >
                <a
                  href={path.href}
                  className={`group block h-full rounded-md border p-7 md:p-8 transition-colors ${
                    path.featured
                      ? "border-sky/40 bg-sky/10 hover:bg-sky/15"
                      : "border-line bg-deep hover:border-sky/30"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-md bg-sky/10 border border-sky/30 flex items-center justify-center text-sky shrink-0">
                      <Icon className="w-6 h-6" />
                    </div>

                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-sky font-mono">
                        {path.eyebrow}
                      </div>

                      <h2 className="mt-2 font-display text-ice text-2xl font-bold tracking-[-0.015em]">
                        {path.title}
                      </h2>
                    </div>
                  </div>

                  <p className="mt-5 text-slate text-[15px] leading-relaxed">
                    {path.body}
                  </p>

                  <div className="mt-6 text-sky font-semibold text-sm inline-flex items-center gap-2">
                    {path.cta}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </a>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Talent pool */}
      <section
        id="talent-pool"
        className="bg-deep border-y border-line scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="chapter-mark">
                <BadgeCheck className="w-3.5 h-3.5" />
                Employer-Ready Talent Pool
              </div>

              <h2 className="mt-6 font-display text-ice text-3xl md:text-5xl tracking-[-0.022em] font-extrabold leading-[1.05] max-w-4xl">
                Pass the gateways.{" "}
                <span className="text-sky">
                  Become visible
                </span>{" "}
                to approved employers.
              </h2>

              <p className="mt-5 text-slate text-lg leading-[1.65] max-w-3xl">
                The pool is designed for high-volume operational
                roles where employers need stronger evidence before
                deciding who to progress. Your profile only becomes
                available after you have passed every compulsory
                stage.
              </p>
            </div>

            <div className="lg:col-span-5 rounded-md border border-gold/30 bg-gold/10 p-6 md:p-7">
              <div className="text-gold text-[11px] uppercase tracking-[0.18em] font-semibold">
                What successful entry means
              </div>

              <h3 className="mt-3 font-display text-ice text-2xl font-bold">
                Your evidence can be searched by role and location.
              </h3>

              <p className="mt-3 text-slate text-sm leading-relaxed">
                Approved employers can identify candidates by role,
                travel time, availability and assessment evidence,
                then decide who they want to progress into their own
                recruitment process.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  "Role matched",
                  "Travel matched",
                  "Fully screened",
                  "Human reviewed",
                ].map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-gold/25 bg-nav/40 px-3 py-1.5 text-[11px] text-ice"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {TALENT_GATEWAYS.map((gateway, index) => {
              const Icon = gateway.icon;

              return (
                <Reveal
                  key={gateway.number}
                  delay={index * 0.05}
                  className="h-full"
                >
                  <article
                    className="paper-card p-6 md:p-7 h-full"
                    data-testid={`talent-gateway-${gateway.number}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="w-12 h-12 rounded-md bg-sky/10 border border-sky/30 flex items-center justify-center text-sky">
                        <Icon className="w-5 h-5" />
                      </div>

                      <span className="font-mono text-slate text-xs">
                        GATEWAY {gateway.number}
                      </span>
                    </div>

                    <h3 className="mt-5 font-display text-ice text-xl font-bold tracking-[-0.012em]">
                      {gateway.title}
                    </h3>

                    <p className="mt-3 text-slate text-[14px] leading-relaxed">
                      {gateway.body}
                    </p>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-10 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 paper-card p-7 md:p-8">
              <div className="flex items-center gap-2 text-sky text-[11px] uppercase tracking-[0.18em] font-semibold">
                <Video className="w-4 h-4" />
                Final validation interview
              </div>

              <h3 className="mt-3 font-display text-ice text-2xl md:text-3xl font-bold tracking-[-0.018em]">
                A real recruiting manager makes the final
                recommendation.
              </h3>

              <p className="mt-4 text-slate text-[15px] leading-relaxed">
                The final 45–60 minute Teams interview tests the
                authenticity, depth and consistency of your evidence.
                Questions are informed by your CV, simulator answers
                and earlier profile information.
              </p>

              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {[
                  "Role-specific structured questions",
                  "CV and simulator follow-up",
                  "Recorded video and transcript",
                  "Quality-assured admission decision",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex gap-3 rounded-md border border-line bg-nav/40 p-4 text-sm text-slate"
                  >
                    <CheckCircle2 className="w-4 h-4 text-sky mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 rounded-md border border-sky/30 bg-sky/10 p-7 md:p-8">
              <div className="text-sky text-[11px] uppercase tracking-[0.18em] font-semibold">
                Your outcome
              </div>

              <h3 className="mt-3 font-display text-ice text-2xl font-bold">
                Pass means entry. Not passing means no employer
                access.
              </h3>

              <p className="mt-3 text-slate text-sm leading-relaxed">
                There is no premium membership or paid override.
                Candidates who do not meet a compulsory standard do
                not enter the employer-facing pool.
              </p>

              <Link
                to={TALENT_POOL_CONTACT}
                className="mt-6 pill pill-primary"
                data-testid="talent-pool-interest"
              >
                Register interest
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="mt-10 rounded-md border border-sky/30 bg-sky/10 p-6 md:p-7" data-testid="talent-stage-one-spec">
            <div className="text-[11px] uppercase tracking-[0.18em] text-sky font-semibold">
              Stage One: Railway Talent Pool Eligibility Gateway
            </div>
            <p className="mt-3 text-slate text-sm leading-relaxed">
              This first assessment confirms whether you meet the general requirements to enter the Railway Careers Talent Pool.
              It does not assess you for a specific job.
            </p>
            <div className="mt-4 text-sm text-slate">To pass Stage One, you should normally be able to confirm that you:</div>
            <ul className="mt-3 space-y-2 text-sm text-slate">
              {STAGE_ONE_REQUIREMENTS.map((item) => (
                <li key={item} className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-sky shrink-0" />
                  <span>{item};</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-slate text-sm leading-relaxed">
              Previous railway experience is not required for most entry-level pathways. A university degree is not normally required, although some technical or specialist roles may require specific qualifications.
            </p>
            <p className="mt-2 text-slate text-sm leading-relaxed">
              A medical condition, disability or previous conviction does not automatically prevent railway employment. Suitability depends on the role, safety requirements and individual circumstances.
            </p>
            <div className="mt-4 rounded-md border border-line bg-nav/40 p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-sky font-semibold">Stage One outcome</div>
              <p className="mt-2 text-sm text-slate">
                Applicants who meet the general eligibility standard progress to Stage Two: Target Role Selection, where suitability is assessed against specific pathway requirements.
              </p>
              <p className="mt-2 text-sm text-slate">
                Stage 2 is coming soon.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fair entry */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <SectionHeading
          mark="Fair Entry Principles"
          icon={ShieldCheck}
          title="A credible pool depends on"
          accent="consistent standards."
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {POOL_PRINCIPLES.map((principle, index) => {
            const Icon = principle.icon;

            return (
              <Reveal
                key={principle.title}
                delay={index * 0.06}
              >
                <article className="paper-card p-6 h-full">
                  <div className="w-12 h-12 rounded-md bg-sky/10 border border-sky/30 flex items-center justify-center text-sky">
                    <Icon className="w-5 h-5" />
                  </div>

                  <h3 className="mt-4 font-display text-ice text-lg font-bold">
                    {principle.title}
                  </h3>

                  <p className="mt-2 text-slate text-[14px] leading-relaxed">
                    {principle.body}
                  </p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Profile evidence */}
      <section className="bg-deep border-y border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="paper-card p-7 md:p-8">
              <div className="flex items-center gap-2 text-sky text-[11px] uppercase tracking-[0.18em] font-semibold">
                <BriefcaseBusiness className="w-4 h-4" />
                What approved employers can see
              </div>

              <h2 className="mt-3 font-display text-ice text-2xl md:text-3xl font-bold tracking-[-0.018em]">
                A structured evidence profile, not just a CV.
              </h2>

              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {EMPLOYER_VISIBLE.map((item) => (
                  <div
                    key={item}
                    className="flex gap-3 rounded-md border border-line bg-nav/40 p-4 text-sm text-slate"
                  >
                    <CheckCircle2 className="w-4 h-4 text-sky mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4">
              {CANDIDATE_CONTROLS.map((item, index) => {
                const Icon = item.icon;

                return (
                  <Reveal
                    key={item.title}
                    delay={index * 0.05}
                  >
                    <article className="paper-card p-6">
                      <div className="flex gap-4">
                        <div className="w-11 h-11 rounded-md bg-sky/10 border border-sky/30 flex items-center justify-center text-sky shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>

                        <div>
                          <h3 className="font-display text-ice text-lg font-bold">
                            {item.title}
                          </h3>

                          <p className="mt-2 text-slate text-sm leading-relaxed">
                            {item.body}
                          </p>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="mt-8 rounded-md border border-line bg-nav/50 p-5 text-sm text-slate leading-relaxed">
            <strong className="text-ice">Important:</strong>{" "}
            Talent-pool pre-screening does not replace an
            employer&apos;s own application, interview, formal
            occupational assessment, medical, background checks or
            final selection decision. Entry to the pool is not a job
            guarantee.
          </div>
        </div>
      </section>

      {/* Free guides */}
      <section
        id="free-guides"
        className="max-w-7xl mx-auto px-6 md:px-12 py-20 scroll-mt-24"
      >
        <SectionHeading
          mark="Free Applicant Guides"
          icon={Download}
          title="Start with practical guidance that is"
          accent="free to use."
          body="Download concise, no-gate guides covering CV evidence, interviews, role aptitude and online applications. Use them whether you apply directly to an employer or later enter the talent-pool stream."
        />

        <div className="grid md:grid-cols-2 gap-5">
          {FREE_GUIDES.map((resource, index) => (
            <Reveal
              key={resource.label}
              delay={index * 0.06}
            >
              <a
                href={resource.file}
                download={resource.label}
                data-testid={`resource-download-${index}`}
                className="group paper-card p-7 flex gap-5 h-full"
              >
                <div className="w-14 h-14 rounded-md bg-sky/10 border border-sky/30 flex items-center justify-center text-sky shrink-0 group-hover:bg-sky group-hover:text-nav transition-colors">
                  <FileText className="w-6 h-6" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-sky font-mono">
                    <CheckCircle2 className="w-3 h-3" />
                    Free guide · {resource.pages}
                  </div>

                  <h3 className="font-display text-ice text-xl mt-2 font-bold tracking-[-0.015em] leading-tight">
                    {resource.title}
                  </h3>

                  <p className="text-slate text-[14px] mt-3 leading-relaxed">
                    {resource.blurb}
                  </p>

                  <div className="mt-5 flex items-center justify-between gap-4 text-[13px]">
                    <span className="text-sky font-semibold inline-flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Download PDF
                    </span>

                    <span className="text-slate font-mono text-[11px] truncate">
                      {resource.label}
                    </span>
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 paper-card p-8 md:p-10 mesh-bg flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-sky text-[11px] uppercase tracking-[0.16em] font-semibold">
              <BookOpen className="w-4 h-4" />
              Vacancy and guidance updates
            </div>

            <h2 className="mt-3 font-display text-ice text-2xl md:text-3xl tracking-[-0.018em] font-bold leading-tight">
              Subscribe to the{" "}
              <span className="text-sky">
                Monday Briefing.
              </span>
            </h2>

            <p className="mt-2 text-slate text-[14px]">
              Receive UK rail vacancy updates, application guidance
              and talent-pool announcements.
            </p>
          </div>

          <Link
            to="/#newsletter"
            className="pill pill-primary shrink-0"
            data-testid="resource-newsletter-cta"
          >
            Subscribe
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ and CTA */}
      <section className="bg-deep border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <SectionHeading
            mark="Applicant Questions"
            title="Understand the stream"
            accent="before you enter."
          />

          <div className="grid md:grid-cols-2 gap-5">
            {FAQS.map((item, index) => (
              <Reveal
                key={item.q}
                delay={index * 0.05}
              >
                <article className="paper-card p-6 md:p-7 h-full">
                  <h3 className="font-display text-ice text-xl font-bold">
                    {item.q}
                  </h3>

                  <p className="mt-3 text-slate text-sm leading-relaxed">
                    {item.a}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 rounded-md border border-sky/30 bg-sky/10 p-7 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="text-sky text-[11px] uppercase tracking-[0.18em] font-semibold">
                Talent pool interest
              </div>

              <h2 className="mt-2 font-display text-ice text-2xl md:text-3xl font-bold">
                Be notified when your role stream opens.
              </h2>

              <p className="mt-2 text-slate text-sm leading-relaxed max-w-2xl">
                Tell us which operational roles and locations
                interest you. Registering interest does not place you
                into the pool.
              </p>
            </div>

            <Link
              to={TALENT_POOL_CONTACT}
              className="pill pill-primary shrink-0"
            >
              Register interest
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/talent-pool/login"
              className="pill pill-ghost shrink-0"
              data-testid="talent-pool-signin-footer"
            >
              Sign in to dashboard
              <UserCheck className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}